import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PasswordInput, Button, Stack, Alert, Text, Anchor } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import { vendorsApi } from '../../../api/vendors';
import { StatusIcon, VendorAuthLayout } from './VendorAuthLayout';
import { BRAND_MEDIUM } from '../../../theme/brand';

const BACK_TO_LOGIN = { label: 'Back to sign in', to: '/vendor/login' };
const INVALID_LINK = 'This reset link is invalid or has expired. Request a new one to continue.';

export function VendorResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: {
      password: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return 'Please confirm your password';
        if (value !== values.password) return 'Passwords do not match';
        return null;
      },
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (!token) {
      setErrorMessage(INVALID_LINK);
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      await vendorsApi.resetPassword(token, values.password);
      setStatus('success');
      notifications.show({
        title: 'Password Reset Successful',
        message: 'Your password has been reset. You can now log in with your new password.',
        color: 'green',
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/vendor/login');
      }, 3000);
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.response?.data?.error || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <VendorAuthLayout
        back={BACK_TO_LOGIN}
        icon={
          <StatusIcon>
            <IconCircleCheck size={30} aria-hidden />
          </StatusIcon>
        }
        title="Password updated"
        subtitle="You can now sign in with your new password. Taking you to sign in…"
      >
        <Button component={Link} to="/vendor/login" color={BRAND_MEDIUM} fullWidth size="md" radius="md">
          Sign in now
        </Button>
      </VendorAuthLayout>
    );
  }

  const shownError = !token ? INVALID_LINK : errorMessage;

  return (
    <VendorAuthLayout back={BACK_TO_LOGIN} title="Choose a new password" subtitle="Enter a new password for your vendor account.">
      {shownError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="md" role="alert">
          <Text size="sm">{shownError}</Text>
          <Anchor component={Link} to="/vendor/forgot-password" size="sm" fw={600} mt={6} display="inline-block">
            Request a new link
          </Anchor>
        </Alert>
      )}

      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <PasswordInput
            label="New password"
            placeholder="At least 6 characters"
            description="Use at least 6 characters."
            autoComplete="new-password"
            required
            size="md"
            radius="md"
            disabled={!token}
            {...form.getInputProps('password')}
          />

          <PasswordInput
            label="Confirm new password"
            placeholder="Re-enter your new password"
            autoComplete="new-password"
            required
            size="md"
            radius="md"
            disabled={!token}
            {...form.getInputProps('confirmPassword')}
          />

          <Button type="submit" loading={loading} fullWidth color={BRAND_MEDIUM} disabled={!token} size="md" radius="md">
            Update password
          </Button>
        </Stack>
      </form>

      <Text size="sm" ta="center" c="dimmed">
        Remember your password?{' '}
        <Anchor component={Link} to="/vendor/login" fw={600} c={BRAND_MEDIUM}>
          Sign in
        </Anchor>
      </Text>
    </VendorAuthLayout>
  );
}
